import type { TmsInvoiceRow } from '@numeo/types-main-web-app'
import invoiceApi from '@/services/invoiceApi'

/** What a Create batch or Add to batch wrote: the batch's name and how many invoices it holds now from this selection. */
interface CreatedBatch {
    batchName: string
    invoiceCount: number
}

/**
 * Invoices the selected loads under one batch name, exactly as the Invoices page's Create Batch and Add to Batch do (the same
 * `POST /accountant/broker-invoice/batch`): one invoice per load. Never submits to factoring: that stays the factoring drawer's
 * reviewed step. An empty name lets the server name the batch. Throws the server's message when it refuses.
 */
export const createInvoiceBatch = async (rows: TmsInvoiceRow[], batchName: string, appendToExistingBatch: boolean): Promise<CreatedBatch> => {
    const loadIds = rows.map((row) => row.load_id).filter((id): id is string => Boolean(id))
    if (loadIds.length !== rows.length) throw new Error('Some selected loads could not be read. Refresh the table and try again.')
    const response = await invoiceApi.createBatch({ loadIds, batchName: batchName.trim() || undefined, appendToExistingBatch, submitToFactoring: false })
    if (!response.success) throw new Error(response.message || 'The batch was not created.')
    return { batchName: response.data?.batchName || batchName.trim(), invoiceCount: response.data?.invoices.length ?? loadIds.length }
}

/** The chat's record of the batch, so the thread says what was done. */
export const createdBatchNoteOf = ({ batchName, invoiceCount }: CreatedBatch, appended: boolean): string =>
    `${appended ? 'Added' : 'Created'} ${invoiceCount} ${invoiceCount === 1 ? 'invoice' : 'invoices'} ${appended ? 'to' : 'in'} batch “${batchName}”. ` +
    'They are on the Invoices page under Invoiced, ready to send or submit to factoring.'
